import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import {
  AUTH_STORAGE_KEYS,
  isTokenExpired,
  logout,
  setError,
  setLoading,
  setRole,
  setUser,
  type AuthRole,
  type AuthUser,
} from '@/store/slices/authSlice'

export const useAuthInit = () => {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)

  const [initDone, setInitDone] = useState(false)

  useEffect(() => {
    dispatch(setLoading(true))

    try {
      if (typeof window === 'undefined') {
        dispatch(logout())
        return
      }

      const token = localStorage.getItem(AUTH_STORAGE_KEYS.token)
      const tokenTimestampRaw = localStorage.getItem(
        AUTH_STORAGE_KEYS.tokenTimestamp,
      )
      const roleRaw = localStorage.getItem(AUTH_STORAGE_KEYS.role)
      const userRaw = localStorage.getItem(AUTH_STORAGE_KEYS.user)
      const transport = localStorage.getItem(AUTH_STORAGE_KEYS.transport)
      const cookieSession = transport === 'cookie'

      if (!tokenTimestampRaw) {
        // No completed login session — do NOT call logout(): it clears Redux
        // `role`, and a persisted role should survive a pre-auth re-init.
        dispatch(setError(null))
        const pendingRole: AuthRole | null =
          roleRaw === 'validator'
            ? 'validator'
            : roleRaw === 'creator'
              ? 'creator'
              : null
        if (pendingRole) {
          dispatch(setRole(pendingRole))
        }
        return
      }

      const tokenTimestamp = Number(tokenTimestampRaw)
      if (!Number.isFinite(tokenTimestamp) || isTokenExpired(tokenTimestamp)) {
        dispatch(logout())
        dispatch(setError(null))
        return
      }

      if (cookieSession) {
        if (!userRaw) {
          dispatch(logout())
          dispatch(setError(null))
          return
        }
      } else if (!token?.trim()) {
        dispatch(logout())
        dispatch(setError(null))
        return
      }

      const parsedUser = userRaw ? (JSON.parse(userRaw) as unknown) : null
      const safeUser: AuthUser | null =
        parsedUser &&
        typeof parsedUser === 'object' &&
        typeof (parsedUser as AuthUser).id === 'string' &&
        typeof (parsedUser as AuthUser).email === 'string'
          ? (parsedUser as AuthUser)
          : null

      if (!safeUser) {
        dispatch(logout())
        dispatch(setError(null))
        return
      }

      const safeRole: AuthRole | null =
        roleRaw === 'validator'
          ? 'validator'
          : roleRaw === 'creator'
            ? 'creator'
            : null

      dispatch(setUser(safeUser))
      dispatch(setRole(safeRole))
      dispatch(setError(null))
    } catch {
      dispatch(logout())
    } finally {
      dispatch(setLoading(false))
      setInitDone(true)
    }
  }, [dispatch])

  return { isAuthenticated, initDone }
}
